// Two-D vector and geometry operations

// linear interpolation function
export const lerp = (p1, p2, fraction) => ({
  x: p1.x + fraction * (p2.x - p1.x),
  y: p1.y + fraction * (p2.y - p1.y),
});

export const dist = (p1, p2) =>
  Math.sqrt((p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y));
export const dist2 = (p1, p2) =>
  (p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y);

export const vec = (x, y) => ({ x, y });
export const add = (v1, v2) => ({ x: v1.x + v2.x, y: v1.y + v2.y });
export const sub = (v1, v2) => ({ x: v1.x - v2.x, y: v1.y - v2.y });
export const mul = (v, s) => ({ x: v.x * s, y: v.y * s });
export const div = (v, s) => ({ x: v.x / s, y: v.y / s });

// aka "perp dot"
// perpendicular dot product between v1 and v2 where v1 is replaced by perpendicular vector rotated left
export const cross = (v1, v2) => v1.x * v2.y - v1.y * v2.x;
export const dot = (v1, v2) => v1.x * v2.x + v1.y * v2.y;
export const mag = v => Math.sqrt(v.x * v.x + v.y * v.y);
export const norm = v => div(v, mag(v));

export const angle = (v1, v2) => Math.atan2(v2.y - v1.y, v2.x - v1.x);
export const angleDeg = (v1, v2) =>
  (Math.atan2(v2.y - v1.y, v2.x - v1.x) * 180) / Math.PI;

export const normAngle = a => {
  let d = a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d <= -Math.PI) d += Math.PI * 2;
  return d;
};
export const angularDiff = (a1, a2) => normAngle(a1 - a2);

// Return point of intersection between the line segment and
// the ray starting at `origin` and passing through `target`,
// returning: {x: N, y: N} or null if there's no intersection
// or the segment and ray are colinear.
//
// It's "simple" because it returns null even when the segment
// and ray are colinear and overlap.
//
// https://stackoverflow.com/a/14318254/140389
export const simpleIntersectRayWithSegment = (origin, target, seg1, seg2) => {
  const p = origin;
  const r = sub(target, origin);
  const q = seg1;
  const s = sub(seg2, seg1);

  const numerator = cross(sub(q, p), r);
  const denominator = cross(r, s);
  if (denominator === 0) {
    // parallel or colinear
    return null;
  }

  const u = numerator / denominator;
  if (u < 0 || u > 1) return null;
  const t = cross(sub(q, p), s) / denominator;
  if (t >= 0) {
    return lerp(origin, target, t);
  }
  return null;
};

// Return a point that is `dist` from `origin` along the ray
// that starts at `origin` and passes through `target`
export const positionAlongRay = (origin, target, distance) => {
  const dx = dist(origin, target);
  if (dx < 0.00001 && dx > -0.00001) return target;
  return lerp(origin, target, distance / dx);
};
